import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, get, push, set, update, remove } from "firebase/database";
import { db } from "../firebase";
import "./area.css";

// Cache for area data
const areaCache = new Map();
const customersCache = new Map();

export default function AddCustomerPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [areaId, setAreaId] = useState(null);
  const [areaMeta, setAreaMeta] = useState(null);
  const [loadingArea, setLoadingArea] = useState(true);

  // ✅ OPTIMIZED: Use get() instead of onValue() for one-time read
  useEffect(() => {
    const fetchArea = async () => {
      setLoadingArea(true);
      
      // Check cache first
      if (areaCache.has(slug)) {
        const cached = areaCache.get(slug);
        setAreaId(cached.id);
        setAreaMeta(cached.meta);
        setLoadingArea(false);
        return;
      }
      
      try {
        const areasRef = ref(db, "areas");
        const snapshot = await get(areasRef);
        const val = snapshot.val() || {};
        
        const found = Object.entries(val).find(
          ([, v]) => v && v.meta && v.meta.slug === slug
        );
        
        if (found) {
          const areaData = {
            id: found[0],
            meta: found[1].meta || null
          };
          setAreaId(areaData.id);
          setAreaMeta(areaData.meta);
          
          // Cache the result
          areaCache.set(slug, areaData);
          setTimeout(() => areaCache.delete(slug), 5 * 60 * 1000);
        } else {
          setAreaId(null);
          setAreaMeta(null);
        }
      } catch (err) {
        console.error("areas read error:", err);
      } finally {
        setLoadingArea(false);
      }
    };
    
    fetchArea();
  }, [slug]);

  if (loadingArea)
    return (
      <div className="container">
        <div className="card">Loading area...</div>
      </div>
    );

  if (!areaId)
    return (
      <div className="container">
        <div className="card">
          Area not found for slug: <b>{slug}</b>
        </div>
      </div>
    );

  return (
    <div className="container">
      <div className="page-header">
        <h2>Manage Customers — {areaMeta?.name || slug}</h2>
        <button onClick={() => navigate(-1)} className="btn-nav">
          Back
        </button>
      </div>

      <div className="card">
        <CustomerForm areaId={areaId} areaName={areaMeta?.name} onCustomerAdded={() => {
          // Clear cache and refresh
          customersCache.delete(areaId);
          window.dispatchEvent(new Event('customerAdded'));
        }} />
      </div>

      <div className="card">
        <CustomerList areaId={areaId} areaName={areaMeta?.name} />
      </div>
    </div>
  );
}

/* ------------------------------
   Customer Form (Optimized)
--------------------------------*/
function CustomerForm({ areaId, areaName, onCustomerAdded }) {
  const [name, setName] = useState("");
  const [securityMoney, setSecurityMoney] = useState("");
  const [mode, setMode] = useState("daily");
  const [price, setPrice] = useState("");
  const [type, setType] = useState("jar");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const resetForm = () => {
    setName("");
    setSecurityMoney("");
    setMode("daily");
    setPrice("");
    setType("jar");
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!areaId) return setMsg("Area not selected.");
    if (!name.trim() || !price) return setMsg("Please fill all fields.");

    setSaving(true);
    try {
      const newCustRef = push(ref(db, `areas/${areaId}/customers`));
      await set(newCustRef, {
        name: name.trim(),
        securityMoney: Number(securityMoney || 0),
        mode,
        price: Number(price || 0),
        type,
        pendingJars: 0,
        pendingAmount: 0,
        advance: 0,
        createdAt: Date.now(),
      });
      setMsg("✅ Customer added successfully.");
      resetForm();
      onCustomerAdded?.();
    } catch (err) {
      console.error(err);
      setMsg("❌ Error adding customer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="form" onSubmit={handleAdd}>
      <h3>Add New Customer</h3>

      <div className="form-row">
        <label>Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Customer name"
        />
      </div>

      <div className="form-row">
        <label>Security Deposit</label>
        <input
          type="number"
          value={securityMoney}
          onChange={(e) => setSecurityMoney(e.target.value)}
          placeholder="0"
        />
      </div>

      <div className="form-row">
        <label>Price (₹)</label>
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="0"
        />
      </div>

      <div className="form-row">
        <label>Billing Mode</label>
        <select value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      <div className="form-row">
        <label>Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="jar">Jar</option>
          <option value="cooljar">Cool Jar</option>
        </select>
      </div>

      <button className="btn-primary" type="submit" disabled={saving}>
        {saving ? "Saving..." : "Add Customer"}
      </button>

      {msg && <div className="status">{msg}</div>}
    </form>
  );
}

/* ------------------------------
   Customer List (All Customers with Dropdown)
--------------------------------*/
function CustomerList({ areaId, areaName }) {
  const [customers, setCustomers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  // ✅ Load ALL customers at once (optimized with caching)
  const loadAllCustomers = useCallback(async () => {
    if (!areaId) return;
    
    setLoading(true);
    
    // Check cache first
    if (customersCache.has(areaId)) {
      const cached = customersCache.get(areaId);
      setCustomers(cached);
      setLoading(false);
      return;
    }
    
    try {
      const customersRef = ref(db, `areas/${areaId}/customers`);
      const snapshot = await get(customersRef);
      const data = snapshot.val() || {};
      const customersArray = Object.entries(data).map(([id, c]) => ({ id, ...c }));
      
      // Sort customers by name for better UX
      customersArray.sort((a, b) => a.name.localeCompare(b.name));
      
      setCustomers(customersArray);
      
      // Cache the results (cache for 2 minutes)
      customersCache.set(areaId, customersArray);
      setTimeout(() => customersCache.delete(areaId), 2 * 60 * 1000);
      
    } catch (err) {
      console.error("Error loading customers:", err);
      setMsg("❌ Error loading customers");
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  // Filter customers based on search term
  const filteredCustomers = searchTerm.trim() === "" 
    ? customers 
    : customers.filter(customer => 
        customer.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

  // Handle dropdown selection
  useEffect(() => {
    if (selectedCustomerId && filteredCustomers.length > 0) {
      const selectedCustomer = filteredCustomers.find(c => c.id === selectedCustomerId);
      if (selectedCustomer) {
        // Scroll to the selected customer
        const element = document.getElementById(`customer-${selectedCustomerId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.backgroundColor = '#fff3cd';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }
    }
  }, [selectedCustomerId, filteredCustomers]);

  // Initial load
  useEffect(() => {
    if (!areaId) return;
    loadAllCustomers();
  }, [areaId, loadAllCustomers]);

  // Listen for customer added event
  useEffect(() => {
    const handleCustomerAdded = () => {
      customersCache.delete(areaId);
      loadAllCustomers();
    };
    
    window.addEventListener('customerAdded', handleCustomerAdded);
    return () => window.removeEventListener('customerAdded', handleCustomerAdded);
  }, [areaId, loadAllCustomers]);

  const handleDelete = async (id) => {
    if (window.confirm(`Delete "${customers.find(c => c.id === id)?.name}"?`)) {
      try {
        await remove(ref(db, `areas/${areaId}/customers/${id}`));
        customersCache.delete(areaId);
        await loadAllCustomers();
        setMsg("✅ Customer deleted.");
        setTimeout(() => setMsg(""), 2000);
      } catch (err) {
        setMsg("❌ Error deleting customer.");
      }
    }
  };

  const saveEdit = async (id) => {
    try {
      await update(ref(db, `areas/${areaId}/customers/${id}`), form);
      customersCache.delete(areaId);
      await loadAllCustomers();
      setEditId(null);
      setMsg("✅ Customer updated.");
      setTimeout(() => setMsg(""), 2000);
    } catch (err) {
      setMsg("❌ Error updating customer.");
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Name', 'Security Deposit', 'Mode', 'Type', 'Price', 'Pending Jars', 'Pending Amount', 'Advance'];
    const csvData = filteredCustomers.map(c => [
      c.name,
      c.securityMoney || 0,
      c.mode,
      c.type,
      c.price,
      c.pendingJars || 0,
      c.pendingAmount || 0,
      c.advance || 0
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${areaName}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h3>Customer List ({filteredCustomers.length} customers)</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="🔍 Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px',
              width: '200px'
            }}
          />
          <button
            onClick={exportToCSV}
            style={{
              padding: '8px 12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Customer Dropdown for quick navigation */}
      {filteredCustomers.length > 0 && (
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: '600' }}>🎯 Jump to Customer:</label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '14px',
                minWidth: '250px',
                flex: 1
              }}
            >
              <option value="">-- Select a customer --</option>
              {filteredCustomers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.type === 'cooljar' ? 'Cool Jar' : 'Jar'} (₹{customer.price}) - {customer.mode}
                </option>
              ))}
            </select>
            
            {selectedCustomerId && (
              <>
                <button
                  onClick={() => {
                    const customer = filteredCustomers.find(c => c.id === selectedCustomerId);
                    if (customer) {
                      setEditId(selectedCustomerId);
                      setForm(customer);
                      setSelectedCustomerId('');
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  ✏️ Edit Selected
                </button>
                <button
                  onClick={() => {
                    setSelectedCustomerId('');
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p>Loading all customers...</p>
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', minWidth: '800px' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Security</th>
                <th>Mode</th>
                <th>Type</th>
                <th>Price</th>
                <th>Pending Jars</th>
                <th>Pending Amt</th>
                <th>Advance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    {searchTerm ? `No customers found matching "${searchTerm}"` : "No customers found."}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((c, index) => (
                  <tr key={c.id} id={`customer-${c.id}`} style={{ transition: 'background-color 0.3s' }}>
                    <td>{index + 1}</td>
                    <td>
                      {editId === c.id ? (
                        <input
                          value={form.name || ''}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          style={{ width: '100%', padding: '4px' }}
                        />
                      ) : (
                        <strong>{c.name}</strong>
                      )}
                    </td>
                    <td>
                      {editId === c.id ? (
                        <input
                          type="number"
                          value={form.securityMoney || 0}
                          onChange={(e) =>
                            setForm({ ...form, securityMoney: e.target.value })
                          }
                          style={{ width: '100%', padding: '4px' }}
                        />
                      ) : (
                        `₹${c.securityMoney || 0}`
                      )}
                    </td>
                    <td>
                      {editId === c.id ? (
                        <select
                          value={form.mode || 'daily'}
                          onChange={(e) => setForm({ ...form, mode: e.target.value })}
                          style={{ width: '100%', padding: '4px' }}
                        >
                          <option value="daily">Daily</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      ) : (
                        c.mode === 'daily' ? '📅 Daily' : '📆 Monthly'
                      )}
                    </td>
                    <td>
                      {editId === c.id ? (
                        <select
                          value={form.type || 'jar'}
                          onChange={(e) => setForm({ ...form, type: e.target.value })}
                          style={{ width: '100%', padding: '4px' }}
                        >
                          <option value="jar">Jar</option>
                          <option value="cooljar">Cool Jar</option>
                        </select>
                      ) : (
                        c.type === 'cooljar' ? '🥤 Cool Jar' : '🏺 Jar'
                      )}
                    </td>
                    <td>
                      {editId === c.id ? (
                        <input
                          type="number"
                          value={form.price || 0}
                          onChange={(e) => setForm({ ...form, price: e.target.value })}
                          style={{ width: '100%', padding: '4px' }}
                        />
                      ) : (
                        `₹${c.price}`
                      )}
                    </td>
                    <td>{c.pendingJars || 0}</td>
                    <td style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      ₹{c.pendingAmount || 0}
                    </td>
                    <td style={{ color: '#28a745', fontWeight: 'bold' }}>
                      ₹{c.advance || 0}
                    </td>
                    <td>
                      {editId === c.id ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            className="btn-save" 
                            onClick={() => saveEdit(c.id)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            💾 Save
                          </button>
                          <button 
                            className="btn-cancel" 
                            onClick={() => setEditId(null)}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="btn-edit"
                            onClick={() => {
                              setEditId(c.id);
                              setForm(c);
                            }}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            ✏️ Edit
                          </button>
                          <button 
                            className="btn-delete" 
                            onClick={() => handleDelete(c.id)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {msg && <div className="status">{msg}</div>}

      {/* Add spinner CSS */}
      <style>{`
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .btn-cancel:hover {
          background-color: #5a6268 !important;
        }
        
        .table-container {
          max-height: 600px;
          overflow-y: auto;
        }
        
        .table th {
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }
      `}</style>
    </div>
  );
}